import React, { Fragment, useState, useEffect } from "react";
import { useIdleTimer } from "react-idle-timer";
import { useNavigate } from "react-router";

import { useSessionStore } from "~/stores";

import { pathname } from "~/utils";
import { msalInstance } from "~/api/auth/msalClient";

// Own components
import Modal from "~/components/Shared/Modal";
/**
 * Props type
 */
interface Props {
    itemRef: React.RefObject<HTMLDivElement | null>;
}

const SystemTimer: React.FC<Props> = ({ itemRef }) => {
    const navigate = useNavigate();

    useEffect(() => {
        if (itemRef.current) {
            itemRef.current.focus();
        }
    }, [itemRef]);

    // 300000 milliseconds = 5 minutes
    const IDLE_WARNING_TIMEOUT_OFFSET = 300000;
    // 900000 milliseconds = 15 minutes
    const IDLE_TIMEOUT = 900000;
    const [remainingTime, setRemainingTime] = useState(IDLE_TIMEOUT);
    const [showIdleWarning, setShowIdleWarning] = useState(false);
    const {
        sessionTimeout: isSessionTimeoutImminent,
        setSessionTimeout: setIsSessionTimeoutImminent,
    } = useSessionStore();

    // Refs for page visibility handling
    const intervalRef = React.useRef<NodeJS.Timeout | null>(null);
    const backupTimerRef = React.useRef<NodeJS.Timeout | null>(null);
    const isVisibleRef = React.useRef(true);
    // Track last user activity monotonically to avoid library internal resets affecting countdown display
    const lastActivityRef = React.useRef<number>(Date.now());

    const { getRemainingTime, start } = useIdleTimer({
        onIdle: () => {
            setShowIdleWarning(false);
            setIsSessionTimeoutImminent(false); // clear flag
            navigate("/inactive");
        },
        onAction: async () => {
            lastActivityRef.current = Date.now();
            if (showIdleWarning) {
                setShowIdleWarning(false);
                setIsSessionTimeoutImminent(false); // resume polling

                // Perform silent login to refresh the authentication token
                try {
                    const activeAccount = msalInstance.getActiveAccount();
                    if (activeAccount) {
                        await msalInstance.ssoSilent({
                            scopes: ["openid", "profile", "user.read"],
                            account: activeAccount,
                        });
                    }
                } catch (error) {
                    console.error(
                        "Failed to refresh token on user activity:",
                        error,
                    );
                    // Continue anyway - token refresh is non-blocking
                }

                setTimeout(() => {
                    setRemainingTime(IDLE_TIMEOUT);
                }, 1000);
            }
            localStorage.removeItem("sessionExpired");
        },
        timeout: IDLE_TIMEOUT,
        crossTab: true,
        syncTimers: 500,
        eventsThrottle: 1000,
        events: ["mousedown", "touchstart", "MSPointerDown"],
        disabled:
            pathname?.includes("logout") || pathname?.includes("inactive"),
        stopOnIdle: true,
    });

    // Enhanced logout checker that works independently of main timer
    const mountTimeRef = React.useRef<number>(Date.now());
    const timerInitializedRef = React.useRef<boolean>(false);
    const forceLogoutCheck = React.useCallback(async () => {
        // Prefer monotonic remaining based on lastActivityRef to avoid jumps
        const libRemaining = getRemainingTime();
        const monotonicRemaining =
            IDLE_TIMEOUT - (Date.now() - lastActivityRef.current);
        const currentRemainingTime =
            monotonicRemaining > 0 && monotonicRemaining <= IDLE_TIMEOUT
                ? monotonicRemaining
                : libRemaining;

        // Mark timer initialized once we see a positive remaining time less than the full timeout
        if (
            !timerInitializedRef.current &&
            currentRemainingTime > 0 &&
            currentRemainingTime <= IDLE_TIMEOUT
        ) {
            timerInitializedRef.current = true;
        }

        // Ignore 0 values that occur during the first few seconds after mount before initialization
        const withinGracePeriod = Date.now() - mountTimeRef.current < 5000; // 5s grace
        if (!timerInitializedRef.current && withinGracePeriod) {
            return false; // treat as initialization noise
        }

        if (
            timerInitializedRef.current &&
            currentRemainingTime <= 0 &&
            currentRemainingTime !== IDLE_TIMEOUT
        ) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (backupTimerRef.current) clearInterval(backupTimerRef.current);

            localStorage.setItem("sessionExpired", Date.now().toString());

            // Use window.location.href for more reliable navigation when tab is inactive
            window.location.href = "/inactive";
            return true;
        }
        return false;
    }, [getRemainingTime, IDLE_TIMEOUT]);

    // Listen for localStorage changes to detect session expiry from other tabs or previous sessions
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === "sessionExpired" && e.newValue) {
                window.location.href = "/inactive";
            }
        };

        window.addEventListener("storage", handleStorageChange);

        // Clear expired flag if we're not on inactive/logout pages - indicates new session
        if (!pathname?.includes("logout") && !pathname?.includes("inactive")) {
            const expired = localStorage.getItem("sessionExpired");
            if (expired) {
                localStorage.removeItem("sessionExpired");
            }
        }

        // Only check for expired sessions if we're already on active pages
        const expired = localStorage.getItem("sessionExpired");
        if (
            expired &&
            (pathname?.includes("logout") || pathname?.includes("inactive"))
        ) {
            return; // Don't redirect if already on inactive page
        } else if (expired) {
            window.location.href = "/inactive";
            return; // Exit early if redirecting
        }

        // Additional validation: Clear the flag if session is valid and timer is initialized
        setTimeout(() => {
            const currentRemainingTime = getRemainingTime();
            if (
                currentRemainingTime > 0 &&
                currentRemainingTime < IDLE_TIMEOUT
            ) {
                localStorage.removeItem("sessionExpired");
            }
        }, 1000); // Wait 1 second for timer to initialize

        return () => {
            window.removeEventListener("storage", handleStorageChange);
        };
    }, [getRemainingTime, IDLE_TIMEOUT, pathname]);

    // Multiple backup timers with different intervals to beat browser throttling
    useEffect(() => {
        if (!pathname?.includes("logout") && !pathname?.includes("inactive")) {
            const runBackupCheck = async () => {
                try {
                    const loggedOut = await forceLogoutCheck();
                    if (!loggedOut) {
                        // Schedule next check with recursive setTimeout (more resistant to throttling)
                        backupTimerRef.current = setTimeout(
                            runBackupCheck,
                            5000,
                        );
                    }
                } catch (error) {
                    console.error(
                        "❌ SystemTimer: Error in backup check:",
                        error,
                    );
                    // Schedule next check anyway
                    backupTimerRef.current = setTimeout(runBackupCheck, 5000);
                }
            };

            // Start the backup timer
            runBackupCheck();

            // Add window focus/blur events for immediate checking
            const handleWindowFocus = async () => {
                try {
                    await forceLogoutCheck();
                } catch (error) {
                    console.error(
                        "❌ SystemTimer: Error in focus check:",
                        error,
                    );
                }
            };

            const handleWindowBlur = () => {
                // Session check will continue in background
            };

            window.addEventListener("focus", handleWindowFocus);
            window.addEventListener("blur", handleWindowBlur);

            return () => {
                if (backupTimerRef.current) {
                    clearTimeout(backupTimerRef.current);
                    backupTimerRef.current = null;
                }
                window.removeEventListener("focus", handleWindowFocus);
                window.removeEventListener("blur", handleWindowBlur);
            };
        }
    }, [forceLogoutCheck, pathname]);

    // Timer control functions
    // Note: Timer continues running even when tab is hidden to stay in sync with server-side session
    const startTimer = React.useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        // Immediate sync before starting interval
        const initialRemainingTime = getRemainingTime();
        setRemainingTime(initialRemainingTime);
        if (initialRemainingTime > 0 && initialRemainingTime < IDLE_TIMEOUT) {
            timerInitializedRef.current = true;
        }

        intervalRef.current = setInterval(() => {
            const currentRemainingTime = getRemainingTime();
            setRemainingTime(currentRemainingTime);

            // Critical: Check for session expiration using the enhanced logout checker
            forceLogoutCheck()
                .then(loggedOut => {
                    if (loggedOut) {
                        return; // Logout handled by forceLogoutCheck
                    }
                })
                .catch(console.error);

            // Restart the timer when it hits 0 (due to stopOnIdle: true)
            if (currentRemainingTime === 0) {
                start();
            }

            const shouldWarn =
                currentRemainingTime > 0 &&
                currentRemainingTime <= IDLE_WARNING_TIMEOUT_OFFSET;
            if (shouldWarn && !showIdleWarning) {
                setShowIdleWarning(true);
                setIsSessionTimeoutImminent(true); // suppress polling elsewhere
            }

            // Only update session timeout state when there's a transition
            // Only toggle imminent flag based on 60s threshold if not already set by warning logic
            if (!showIdleWarning) {
                if (currentRemainingTime < 60000 && !isSessionTimeoutImminent) {
                    setIsSessionTimeoutImminent(true);
                } else if (
                    currentRemainingTime >= 60000 &&
                    isSessionTimeoutImminent
                ) {
                    setIsSessionTimeoutImminent(false);
                }
            }
        }, 1000);
    }, [
        getRemainingTime,
        start,
        isSessionTimeoutImminent,
        setIsSessionTimeoutImminent,
        forceLogoutCheck,
    ]);

    const stopTimer = React.useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    // Resume polling on user action if warning was showing
    useEffect(() => {
        if (
            !showIdleWarning &&
            !intervalRef.current &&
            !pathname?.includes("logout") &&
            !pathname?.includes("inactive")
        ) {
            // When warning dismissed (by action) resume interval if session still active.
            const remaining = getRemainingTime();
            if (remaining > 0) {
                startTimer();
            }
        }
    }, [showIdleWarning, startTimer, getRemainingTime, pathname]);

    // Handle page visibility changes - sync timer when page becomes visible
    useEffect(() => {
        const handleVisibilityChange = () => {
            isVisibleRef.current = !document.hidden;

            if (
                !pathname?.includes("logout") &&
                !pathname?.includes("inactive")
            ) {
                if (isVisibleRef.current) {
                    // Page became visible - sync with actual remaining time immediately
                    const currentRemainingTime = getRemainingTime();

                    // Critical: Check if session expired while tab was hidden
                    forceLogoutCheck()
                        .then(loggedOut => {
                            if (loggedOut) {
                                return; // Logout handled by forceLogoutCheck
                            }

                            // Force immediate sync with the idle timer
                            setRemainingTime(currentRemainingTime);

                            // Restart the timer to ensure consistent intervals
                            startTimer();
                        })
                        .catch(console.error);
                } else {
                    // Page became hidden - continue running but note the state
                    // Don't stop the timer - let it continue to stay in sync with server
                }
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            document.removeEventListener(
                "visibilitychange",
                handleVisibilityChange,
            );
        };
    }, [startTimer, getRemainingTime, forceLogoutCheck]);

    // Initial timer setup
    useEffect(() => {
        if (!pathname?.includes("logout") && !pathname?.includes("inactive")) {
            // Clear any stale session expired flags when starting fresh
            localStorage.removeItem("sessionExpired");
            startTimer();
        }

        return () => {
            stopTimer();
        };
    }, [startTimer, stopTimer]);

    return (
        <Fragment>
            <Modal
                open={showIdleWarning}
                onClose={() => null}
                id="inactive"
                title="Session Timeout"
                smallView
            >
                You will be logged out due to inactivity
                <br />
                in{" "}
                {remainingTime < 60000
                    ? `${Math.floor(remainingTime / 1000)} seconds`
                    : `less than ${Math.ceil(
                          Math.ceil(remainingTime / 60 / 1000),
                      )} minutes`}
                .
            </Modal>
        </Fragment>
    );
};

export default SystemTimer;
